/**
 * Entrada de teclado/mouse. Usa e.code (independe de layout).
 * Distingue "está pressionado" (held) de "pressionou neste frame" (edge) para
 * ações como ataque, dash e pause. Chame update() ao fim de cada frame do jogo.
 */
import * as THREE from "three";

export class Input {
  private held = new Set<string>();
  private pressedThisFrame = new Set<string>();
  private mouseDown = false;
  private mouseClickedThisFrame = false;
  private rightClickedThisFrame = false;
  /** Coordenadas normalizadas do mouse (-1..1), para raycast. */
  readonly mouseNdc = new THREE.Vector2();

  private enabled = true;

  constructor(private target: HTMLElement = document.body) {
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
    this.target.addEventListener("mousedown", this.onMouseDown);
    window.addEventListener("mouseup", this.onMouseUp);
    window.addEventListener("mousemove", this.onMouseMove);
    window.addEventListener("blur", this.onBlur);
    // impede menu de contexto atrapalhar (botão direito futuro)
    this.target.addEventListener("contextmenu", (e) => e.preventDefault());
  }

  /** Desliga a captura de ações de gameplay (ex.: durante pause/menus). Setas/ESC seguem via edge. */
  setEnabled(v: boolean): void {
    this.enabled = v;
    if (!v) {
      this.held.clear();
      this.mouseDown = false;
    }
  }

  isDown(code: string): boolean {
    return this.enabled && this.held.has(code);
  }

  /** Foi pressionado neste frame (edge). Funciona mesmo com gameplay desabilitado (ex.: ESC). */
  pressed(code: string): boolean {
    return this.pressedThisFrame.has(code);
  }

  mouseLeftHeld(): boolean {
    return this.enabled && this.mouseDown;
  }

  mouseLeftPressed(): boolean {
    return this.enabled && this.mouseClickedThisFrame;
  }

  /** Botão direito pressionado neste frame (edge) — usado pelo Hook. */
  mouseRightPressed(): boolean {
    return this.enabled && this.rightClickedThisFrame;
  }

  /** Vetor de movimento bruto a partir de WASD (não normalizado). */
  moveVector(out: { x: number; y: number }): { x: number; y: number } {
    out.x = (this.isDown("KeyD") ? 1 : 0) - (this.isDown("KeyA") ? 1 : 0);
    out.y = (this.isDown("KeyS") ? 1 : 0) - (this.isDown("KeyW") ? 1 : 0);
    return out;
  }

  /** Limpa flags de frame. Chamar no fim do loop. */
  update(): void {
    this.pressedThisFrame.clear();
    this.mouseClickedThisFrame = false;
    this.rightClickedThisFrame = false;
  }

  dispose(): void {
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
    this.target.removeEventListener("mousedown", this.onMouseDown);
    window.removeEventListener("mouseup", this.onMouseUp);
    window.removeEventListener("mousemove", this.onMouseMove);
    window.removeEventListener("blur", this.onBlur);
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    // Typing and native keyboard controls belong to the UI, even while paused.
    const el = e.target instanceof Element ? e.target : null;
    if (el?.closest('input, textarea, select, [contenteditable="true"]')) return;
    if (el?.closest('button, a, [role="button"], [role="checkbox"]') && (e.code === "Space" || e.code.startsWith("Arrow") || e.code === "Enter")) return;
    if (!this.held.has(e.code)) this.pressedThisFrame.add(e.code);
    this.held.add(e.code);
    if (this.enabled && (e.code === "Space" || e.code.startsWith("Arrow"))) e.preventDefault();
  };
  private onKeyUp = (e: KeyboardEvent): void => {
    this.held.delete(e.code);
  };
  private onMouseDown = (e: MouseEvent): void => {
    if (e.button === 0) {
      this.mouseDown = true;
      this.mouseClickedThisFrame = true;
    } else if (e.button === 2) {
      this.rightClickedThisFrame = true;
    }
  };
  private onMouseUp = (e: MouseEvent): void => {
    if (e.button === 0) this.mouseDown = false;
  };
  private onMouseMove = (e: MouseEvent): void => {
    this.mouseNdc.x = (e.clientX / window.innerWidth) * 2 - 1;
    this.mouseNdc.y = -(e.clientY / window.innerHeight) * 2 + 1;
  };
  private onBlur = (): void => {
    this.held.clear();
    this.mouseDown = false;
  };
}
